import React, { useContext, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { BarChart3, Eye, Percent, Table2 } from "lucide-react";
import AppContext from "../../../context/AppContext";
import translations from "../../common/Translations";
import CommonTooltip from "../../common/CommonTooltip";
import ActionIconButton from "../../common/ActionIconButton";
import ChartModal from "../user/static-charts/ChartModal";
import { isRTL } from "../../../utils/rtl";

// Rows drawn before "Show all" is needed; the bar chart grows with every row.
const PREVIEW_LIMIT = 12;
const BAR_ROW_HEIGHT_PX = 34;
const PLOT_CHROME_PX = 56;

const toCount = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
};

const formatNumber = (value) => Number(value || 0).toLocaleString();

const percentOf = (count, total) => (total > 0 ? Math.round((count / total) * 100) : 0);

// A non-zero count never reads "0%".
export const shareText = (count, total) => {
  if (total <= 0 || count <= 0) return "0%";
  const pct = (count / total) * 100;
  return pct < 1 ? "<1%" : `${Math.round(pct)}%`;
};

// Row names and details go into Apex's custom tooltip as raw HTML.
const escapeHtml = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]);

/**
 * A card of horizontal stacked bars, one per row (a group, a store, ...), each
 * split into `series`. Bar / Share / Table views sit behind the eye icon like the
 * governorate chart, the HTML legend mutes a series on click, and long lists are
 * cut to the top PREVIEW_LIMIT rows until "Show all" is pressed.
 *
 * rows:   [{ id, name, total, values: { [seriesKey]: n }, detail?, tooltipLines?: [string] }]
 * series: [{ key, label, icon, color }] -- series that are zero in every row are dropped
 * tiles:  [{ key, text, value, Icon, color?, share? }]
 */
const InventoryStackedBarCard = ({
  title,
  subtitle,
  Icon,
  iconClass = "text-[#236DB4]",
  rows,
  series,
  tiles = [],
  nameHeader,
  totalHeader,
  detailHeader,
  emptyIcon: EmptyIcon,
  emptyMessage,
  barHint,
  tableHint,
  showAllText,
  showTopText,
  footnote,
  testId,
}) => {
  const { theme, langMode } = useContext(AppContext);
  const t = translations[langMode] || translations.en;
  const isDark = theme === "dark" || theme === "high-contrast";
  const isHighContrast = theme === "high-contrast";
  const isRtl = isRTL(langMode);

  const [view, setView] = useState("bar");
  const [showViewModal, setShowViewModal] = useState(false);
  const [mutedKeys, setMutedKeys] = useState([]);
  const [showAll, setShowAll] = useState(false);

  const valueOf = (row, key) => row.values[key] || 0;

  // Series that draw nothing would only add legend noise; the table follows suit.
  const activeSeries = useMemo(() => {
    const present = series.filter((entry) => rows.some((row) => (row.values[entry.key] || 0) > 0));
    return present.length > 0 ? present : series;
  }, [series, rows]);
  const plottedSeries = activeSeries.filter((entry) => !mutedKeys.includes(entry.key));
  const seriesTotals = useMemo(
    () => Object.fromEntries(activeSeries.map((entry) => [entry.key, rows.reduce((sum, row) => sum + (row.values[entry.key] || 0), 0)])),
    [activeSeries, rows]
  );

  const visibleRows = useMemo(() => (showAll ? rows : rows.slice(0, PREVIEW_LIMIT)), [rows, showAll]);
  const hasDetail = Boolean(detailHeader) && rows.some((row) => row.detail);

  const inkPrimary = isHighContrast ? "#FDE047" : isDark ? "#E5E7EB" : "#334155";
  const inkMuted = isHighContrast ? "#FDE047" : isDark ? "#9CA3AF" : "#64748B";
  const gridLine = isDark ? "#374151" : "#E2E8F0";
  const surface = isHighContrast ? "#000000" : isDark ? "#1F2937" : "#FFFFFF";
  const mutedText = isHighContrast ? "text-yellow-300" : theme === "dark" ? "text-gray-400" : "text-gray-500";
  const subtleBorder = theme === "dark" ? "border-gray-700" : isHighContrast ? "border-yellow-500/40" : "border-slate-100";
  const cardClass = `rounded-2xl border shadow-md hover:shadow-lg transition-shadow duration-300 ease-in-out p-3 sm:p-4 w-full min-w-0 flex flex-col ${
    theme === "dark"
      ? "bg-gray-800 border-gray-700 text-gray-200"
      : isHighContrast
        ? "bg-black border-2 border-yellow-500 text-yellow-300"
        : "bg-white border-gray-200/70 text-gray-800"
  }`;
  const tileClass = `rounded-xl border px-2.5 py-1.5 text-start ${
    theme === "dark" ? "border-gray-700 bg-gray-900/40" : isHighContrast ? "border-yellow-500/40 bg-black" : "border-slate-100 bg-slate-50/70"
  }`;

  const percentMode = view === "share";
  const chartOptions = useMemo(() => {
    const widestTotal = visibleRows.reduce((max, row) => Math.max(max, row.total), 0);
    const totalLabelGutterPx = 14 + formatNumber(widestTotal).length * 7;
    return {
      chart: {
        type: "bar",
        stacked: true,
        stackType: percentMode ? "100%" : "normal",
        toolbar: { show: false },
        fontFamily: "inherit",
        background: "transparent",
        animations: { enabled: false },
      },
      colors: plottedSeries.map((entry) => entry.color),
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "58%",
          borderRadius: 4,
          borderRadiusApplication: "end",
          borderRadiusWhenStacked: "last",
          dataLabels: {
            total: {
              enabled: true,
              offsetX: isRtl ? -6 : 6,
              // The row's real total, also in share mode where every bar reads 100.
              formatter: (value, opts) => {
                const row = visibleRows[opts?.dataPointIndex];
                return formatNumber(row ? row.total : value);
              },
              style: { fontSize: "11px", fontWeight: 600, color: inkPrimary },
            },
          },
        },
      },
      stroke: { show: true, width: 2, colors: [surface] },
      // A count inside every segment wide enough to hold one.
      dataLabels: {
        enabled: true,
        formatter: (value, opts) => {
          const entry = plottedSeries[opts?.seriesIndex];
          const row = visibleRows[opts?.dataPointIndex];
          if (!entry || !row) return "";
          const count = row.values[entry.key] || 0;
          return percentOf(count, row.total) >= 8 ? formatNumber(count) : "";
        },
        style: { fontSize: "10px", fontWeight: 600, colors: ["#FFFFFF"] },
        dropShadow: { enabled: false },
      },
      legend: { show: false },
      xaxis: {
        categories: visibleRows.map((row) => row.name || t.notAvailable),
        labels: {
          style: { colors: inkMuted, fontSize: "11px" },
          formatter: (value) => (percentMode ? `${Math.round(toCount(value))}%` : formatNumber(Math.round(toCount(value)))),
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { reversed: isRtl, labels: { maxWidth: 160, style: { colors: inkPrimary, fontSize: "11px" } } },
      grid: {
        borderColor: gridLine,
        strokeDashArray: 3,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: false } },
        padding: { left: isRtl ? totalLabelGutterPx : 4, right: isRtl ? 4 : totalLabelGutterPx, top: 0, bottom: 0 },
      },
      // The whole row on hover: its total, any extra lines, and every series with count and share.
      tooltip: {
        theme: isDark ? "dark" : "light",
        custom: ({ dataPointIndex }) => {
          const row = visibleRows[dataPointIndex];
          if (!row) return "";
          const lines = (row.tooltipLines || [])
            .filter(Boolean)
            .map((line) => `<div style="opacity:.7;margin-bottom:2px">${escapeHtml(line)}</div>`)
            .join("");
          const breakdown = activeSeries
            .map((entry) => {
              const count = row.values[entry.key] || 0;
              return `<div style="display:flex;align-items:center;gap:8px;justify-content:space-between;margin-top:4px">
                  <span style="display:inline-flex;align-items:center;gap:6px">
                    <span style="width:10px;height:10px;border-radius:2px;background:${entry.color}"></span>${escapeHtml(entry.label)}
                  </span>
                  <span style="font-weight:600;font-variant-numeric:tabular-nums">${formatNumber(count)}
                    <span style="opacity:.6;font-weight:500">(${shareText(count, row.total)})</span>
                  </span>
                </div>`;
            })
            .join("");
          return `<div style="padding:8px 10px;min-width:190px;font-size:12px" dir="${isRtl ? "rtl" : "ltr"}">
              <div style="font-weight:600;margin-bottom:2px">${escapeHtml(row.name || t.notAvailable)}</div>
              ${lines}
              <div style="opacity:.7;margin-bottom:4px">${escapeHtml(totalHeader)}: <b>${formatNumber(row.total)}</b></div>
              ${breakdown}
            </div>`;
        },
      },
      states: { active: { filter: { type: "none" } } },
      noData: { text: "" },
    };
  }, [visibleRows, plottedSeries, activeSeries, percentMode, isRtl, isDark, inkPrimary, inkMuted, gridLine, surface, totalHeader, t]);

  const viewOptions = [
    {
      key: "bar",
      value: "bar",
      name: t.barChartView,
      description: barHint,
      icon: <BarChart3 className="h-9 w-9 text-blue-600 transition-colors duration-200" />,
    },
    {
      key: "share",
      value: "share",
      name: t.shareView,
      description: t.shareViewHint,
      icon: <Percent className="h-9 w-9 text-amber-500 transition-colors duration-200" />,
    },
    {
      key: "table",
      value: "table",
      name: t.tableView,
      description: tableHint,
      icon: <Table2 className="h-9 w-9 text-emerald-600 transition-colors duration-200" />,
    },
  ];

  const toggleSeries = (key) =>
    setMutedKeys((current) => (current.includes(key) ? current.filter((k) => k !== key) : [...current, key]));

  let body;
  if (rows.length === 0) {
    body = (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 py-10 text-center">
        <span
          className={`flex h-12 w-12 items-center justify-center rounded-full ${
            isHighContrast ? "bg-black text-yellow-300" : theme === "dark" ? "bg-[#236DB4]/20 text-[#236DB4]" : "bg-[#236DB4]/10 text-[#236DB4]"
          }`}
        >
          {EmptyIcon && <EmptyIcon className="h-6 w-6" />}
        </span>
        <p className={`text-sm ${mutedText}`}>{emptyMessage}</p>
      </div>
    );
  } else if (view === "table") {
    body = (
      <div className="max-h-[420px] min-h-0 flex-1 overflow-auto">
        <table className="w-full min-w-[640px] border-collapse text-start text-xs" data-testid={testId && `${testId}-table`}>
          <thead className={`sticky top-0 z-20 ${theme === "dark" ? "bg-gray-800" : isHighContrast ? "bg-black" : "bg-white"}`}>
            <tr className={mutedText}>
              <th scope="col" className="px-3 py-2.5 text-start font-semibold">{nameHeader}</th>
              {hasDetail && <th scope="col" className="px-3 py-2.5 text-start font-semibold">{detailHeader}</th>}
              {activeSeries.map((entry) => {
                const SeriesIcon = entry.icon;
                return (
                  <th
                    key={entry.key}
                    scope="col"
                    className="whitespace-nowrap px-3 py-2.5 text-end font-semibold"
                    style={{ borderBottom: `2px solid ${entry.color}` }}
                  >
                    <span className="inline-flex items-center gap-1">
                      {SeriesIcon && <SeriesIcon size={11} className="shrink-0" />}
                      {entry.label}
                    </span>
                  </th>
                );
              })}
              <th scope="col" className="whitespace-nowrap px-3 py-2.5 text-end font-semibold">{totalHeader}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className={`border-t ${subtleBorder}`}>
                <th scope="row" className="max-w-[14rem] px-3 py-2.5 text-start font-medium">
                  <CommonTooltip
                    text={row.name || t.notAvailable}
                    position="top"
                    disableDefaultDescription
                    tooltipClassName="w-max max-w-[min(30rem,calc(100vw-1.5rem))] [text-wrap:balance]"
                    fitContent
                    clampToViewport
                    className="max-w-full cursor-default"
                  >
                    <span className="block max-w-[200px] truncate">{row.name || t.notAvailable}</span>
                  </CommonTooltip>
                </th>
                {hasDetail && (
                  <td className={`max-w-[12rem] px-3 py-2.5 ${mutedText}`}>
                    <span className="block truncate" title={row.detail || ""}>{row.detail || "-"}</span>
                  </td>
                )}
                {activeSeries.map((entry) => {
                  const count = valueOf(row, entry.key);
                  return (
                    <td key={entry.key} className="px-3 py-2.5 text-end tabular-nums">
                      {count > 0 ? (
                        <span className="inline-block min-w-[26px] rounded px-1.5 py-0.5 font-medium" style={{ backgroundColor: `${entry.color}24` }}>
                          {formatNumber(count)}
                          <span className={`ms-1 text-[10px] ${mutedText}`}>{shareText(count, row.total)}</span>
                        </span>
                      ) : (
                        <span className={mutedText}>0</span>
                      )}
                    </td>
                  );
                })}
                <td className="px-3 py-2.5 text-end font-semibold tabular-nums">{formatNumber(row.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  } else if (plottedSeries.length === 0) {
    body = <div className={`flex flex-1 items-center justify-center py-10 text-xs ${mutedText}`}>{t.allStatusesHidden}</div>;
  } else {
    body = (
      <div className="w-full" data-testid={testId && `${testId}-chart`}>
        <ReactApexChart
          key={`${plottedSeries.map((entry) => entry.key).join("|")}-${percentMode ? "pct" : "count"}-${isRtl ? "rtl" : "ltr"}-${visibleRows.length}`}
          options={chartOptions}
          series={plottedSeries.map((entry) => ({ name: entry.label, data: visibleRows.map((row) => valueOf(row, entry.key)) }))}
          type="bar"
          width="100%"
          height={Math.max(180, visibleRows.length * BAR_ROW_HEIGHT_PX + PLOT_CHROME_PX)}
        />
      </div>
    );
  }

  return (
    <section className={cardClass} data-testid={testId}>
      <div className="mb-3 flex w-full items-start justify-between gap-2 px-1">
        <div className="min-w-0 flex-1">
          <h3 className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-base font-semibold">
            {Icon && <Icon className={`shrink-0 ${iconClass}`} size={18} />}
            <span className="truncate">{title}</span>
          </h3>
          {subtitle && <p className={`mt-0.5 text-[11px] sm:text-xs ${mutedText}`}>{subtitle}</p>}
        </div>
        <ActionIconButton
          tone="gray"
          className="shrink-0 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          onClick={() => setShowViewModal(true)}
          ariaLabel={t.selectView}
          tooltipText={t.selectView}
          tooltipPosition="top"
        >
          <Eye size={18} />
        </ActionIconButton>
      </div>

      {rows.length > 0 && tiles.length > 0 && (
        <div className={`mb-3 grid grid-cols-2 gap-2 ${tiles.length >= 4 ? "lg:grid-cols-4" : "sm:grid-cols-3"}`}>
          {tiles.map((tile) => (
            <div key={tile.key} className={tileClass}>
              <p className={`flex items-center gap-1 truncate text-[10px] font-semibold uppercase tracking-wide ${mutedText}`}>
                {tile.Icon && <tile.Icon size={11} className="shrink-0" style={tile.color ? { color: tile.color } : undefined} />}
                {tile.text}
              </p>
              <p className="text-base font-semibold leading-tight tabular-nums">
                {formatNumber(tile.value)}
                {tile.share != null && <span className={`ms-1 text-[11px] font-medium ${mutedText}`}>{tile.share}</span>}
              </p>
            </div>
          ))}
        </div>
      )}

      {body}

      {rows.length > 0 && view !== "table" && (
        <div className={`mt-2 flex flex-wrap items-center justify-between gap-2 border-t pt-2 ${subtleBorder}`}>
          {/* Click a series to take it out of the bars; it stays listed, dimmed. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            {activeSeries.map((entry) => {
              const SeriesIcon = entry.icon;
              const isMuted = mutedKeys.includes(entry.key);
              return (
                <button
                  key={entry.key}
                  type="button"
                  onClick={() => toggleSeries(entry.key)}
                  aria-pressed={!isMuted}
                  className={`inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 text-[11px] transition-opacity ${
                    isMuted ? "opacity-40" : "opacity-100"
                  }`}
                >
                  <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: entry.color }} />
                  {SeriesIcon && <SeriesIcon size={12} className={`shrink-0 ${mutedText}`} />}
                  <span className={`${mutedText} ${isMuted ? "line-through" : ""}`}>
                    {entry.label} · {formatNumber(seriesTotals[entry.key])}
                  </span>
                </button>
              );
            })}
          </div>
          {rows.length > PREVIEW_LIMIT && showAllText && (
            <button
              type="button"
              onClick={() => setShowAll((value) => !value)}
              className="text-xs font-medium text-[#236DB4] hover:underline"
              data-testid={testId && `${testId}-toggle`}
            >
              {showAll
                ? showTopText.replace("{count}", PREVIEW_LIMIT)
                : showAllText.replace("{count}", rows.length)}
            </button>
          )}
        </div>
      )}

      {rows.length > 0 && footnote && <p className={`mt-2 text-[11px] ${mutedText}`}>{footnote}</p>}

      <ChartModal
        showModal={showViewModal}
        setShowModal={setShowViewModal}
        options={viewOptions}
        value={view}
        onSelect={setView}
        title={t.selectView}
      />
    </section>
  );
};

export default InventoryStackedBarCard;
